import Modal from "bootstrap/js/dist/modal";
import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from "react";
import ReactDOM from "react-dom";
import { isFunction } from "utils/helpers";

const TimeOverlapping = forwardRef(({ isShown, setShown, message, onConfirm, onCancel, isLoading }, ref) => {
	const modalId = useId().replace(/:/g, "");

	// portal dom element
	let portalsRoot = document.getElementById("portals") || null;
	if (!portalsRoot) {
		portalsRoot = document.createElement("div");
		portalsRoot.setAttribute("id", "portals");
		document.body.appendChild(portalsRoot);
	}

	// ref hook
	const modalRef = useRef(null);

	useImperativeHandle(ref, () => ({
		hide: () => {
			const modalElement = modalRef?.current;
			if (!modalElement) return;
			Modal.getInstance(modalElement)?.hide();
			if (isFunction(setShown)) setShown(false);
		},
	}));

	// effect hook — keep a single Bootstrap instance; do not dispose on isShown toggle
	useEffect(() => {
		const modalCurrentElement = modalRef?.current;
		if (!modalCurrentElement) return undefined;

		let modal = Modal.getInstance(modalCurrentElement);
		if (!modal) {
			modal = new Modal(modalCurrentElement);
			modalCurrentElement.addEventListener("hidden.bs.modal", () => {
				if (isFunction(setShown)) setShown(false);
			});
		}

		if (isShown) modal.show();
		else modal.hide();

		return undefined;
	}, [isShown, setShown]);

	useEffect(() => {
		const modalCurrentElement = modalRef?.current;
		return () => {
			if (modalCurrentElement) Modal.getInstance(modalCurrentElement)?.dispose();
		};
	}, []);

	const handleConfirmClick = () => {
		if (isLoading) return;
		if (isFunction(onConfirm)) onConfirm();
	};

	const handleCancelClick = () => {
		if (isFunction(onCancel)) onCancel();
		Modal.getInstance(modalRef?.current)?.hide();
		if (isFunction(setShown)) setShown(false);
	};

	return (
		portalsRoot &&
		ReactDOM.createPortal(
			<div
				className="modal fade"
				ref={modalRef}
				id={`timeOverlappingModal-${modalId}`}
				tabIndex="-1"
				aria-labelledby={`timeOverlappingModalLabel-${modalId}`}
				aria-hidden="true">
				<div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
					<div className="modal-content">
						<div className="modal-header border-0 py-4 text-center">
							<h5 id={`timeOverlappingModalLabel-${modalId}`} className="mb-0 text-uppercase w-100">
								<strong>Heads up!</strong>
							</h5>
						</div>
						<div className="modal-body customized-scroll">
							<p className="mb-0 text-center">{message}</p>
						</div>
						<div className="modal-footer border-0">
							<div className="row gy-3 gy-md-0 justify-content-center w-100">
								<div className="col-12 col-md">
									<button
										type="button"
										className="btn btn-primary w-100 text-uppercase"
										disabled={isLoading}
										onClick={handleConfirmClick}>
										<small>
											<strong>{isLoading ? "Scheduling..." : "Yes, Schedule"}</strong>
										</small>
									</button>
								</div>
								<div className="col-12 col-md">
									<button
										type="button"
										className="btn btn-outline-primary w-100 text-uppercase"
										disabled={isLoading}
										onClick={handleCancelClick}>
										<small>
											<strong>No, Cancel</strong>
										</small>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>,
			portalsRoot
		)
	);
});

TimeOverlapping.displayName = "TimeOverlapping";

export default TimeOverlapping;
